import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { getPatient } from '../lib/adapters';
import { batteryColor } from '../lib/devices';
import { loadDeviceCatalog, deviceLabelFrom, deviceIconFrom } from '../lib/deviceCatalog';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { StatusChip, SeverityDot } from '../components/StatusChip';
import ResidentAvatar from '../components/ResidentAvatar';
import { useRelativeTime, formatDateTime } from '../lib/time';
import AssignmentCards from '../components/AssignmentCards';
import {
  ArrowLeft, Phone, MapPin, Heartbeat, PersonSimpleWalk, BellRinging, DoorOpen,
} from '@phosphor-icons/react';

const eventIcon = (type) => {
  if (type === 'fall') return PersonSimpleWalk;
  if (type === 'sos' || type === 'button_press') return BellRinging;
  if (type === 'door_open' || type === 'door_close') return DoorOpen;
  return Heartbeat;
};

export default function PatientDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const rel = useRelativeTime();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => { loadDeviceCatalog().then(setCatalog).catch(() => setCatalog([])); }, []);

  useEffect(() => {
    let alive = true;
    setError(null);
    setPatient(null);
    getPatient(id)
      .then((p) => {
        if (!alive) return;
        if (!p) setError({ status: 'empty', message: 'Patient returned empty payload.' });
        setPatient(p);
      })
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        // eslint-disable-next-line no-console
        console.error('[PatientDetail] getPatient failed', id, status, e?.message);
        setError({ status, message: e?.response?.data?.detail || e?.message || 'Request failed' });
      });
    api.get(`/patients/${id}/devices`)
      .then((r) => alive && setDevices(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    api.get(`/patients/${id}/events`, { params: { limit: 40 } })
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setEvents(list.filter((e) => e.type !== 'heartbeat'));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [id]);

  if (error) {
    return (
      <div className="space-y-4">
        <Link
          to="/patients"
          data-testid="back-to-patients"
          className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink"
        >
          <ArrowLeft size={16} /> {t('residents_title')}
        </Link>
        <div className="bg-white border border-terracotta-100 rounded-2xl p-8 shadow-soft">
          <h2 className="font-heading text-xl text-terracotta-700">
            {error.status === 404 ? 'Patient not found' : 'Could not load patient'}
          </h2>
          <p className="text-sm text-ink-soft mt-2 font-mono">
            ID: <span data-testid="patient-detail-error-id">{id}</span>
            {error.status ? ` · HTTP ${error.status}` : ''}
          </p>
          {error.message && (
            <p className="text-sm text-ink-soft mt-1">{error.message}</p>
          )}
        </div>
      </div>
    );
  }

  if (!patient) return <div className="text-ink-muted">{t('loading')}</div>;

  return (
    <div className="space-y-8 animate-fade-up">
      {user?.role !== 'family' && (
        <Link
          to="/patients"
          data-testid="back-to-patients"
          className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink transition-colors"
        >
          <ArrowLeft size={16} /> {t('residents_title')}
        </Link>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
        <aside className="bg-bg-alt rounded-2xl border border-stone_line p-8 space-y-6" data-testid="patient-hero">
          <div className="flex items-center gap-4">
            <ResidentAvatar
              name={patient.name}
              src={patient.avatar_url}
              size={80}
              rounded="2xl"
              testid="patient-detail-avatar"
            />
            <div>
              <h1 className="font-heading text-3xl text-ink leading-tight">{patient.name}</h1>
              <div className="text-sm text-ink-soft mt-1">
                {patient.room_id && patient.room_label && patient.room_label !== '—' ? (
                  <Link
                    to={`/rooms/${encodeURIComponent(patient.room_id)}`}
                    data-testid="patient-detail-room-link"
                    className="text-sage-600 hover:text-sage-700"
                  >
                    {t('room')} {patient.room_label}
                  </Link>
                ) : null}
              </div>
              <div className="mt-2"><StatusChip status={patient.status} /></div>
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t border-stone_line">
            {patient.address && (
              <div className="flex items-start gap-3 text-sm text-ink-soft">
                <MapPin size={18} className="text-sage-500 mt-0.5" weight="duotone" />
                <span>{patient.address}</span>
              </div>
            )}
            {(patient.emergency_contacts || []).length > 0 ? (
              <div className="flex items-start gap-3 text-sm text-ink-soft">
                <Phone size={18} className="text-sage-500 mt-0.5" weight="duotone" />
                <div className="space-y-3 min-w-0 flex-1">
                  {patient.emergency_contacts.map((c, i) => (
                    <div key={`${c.name || c.phone}-${i}`} data-testid={`patient-emergency-contact-${i}`}>
                      {c.name && (
                        <div className="text-ink font-medium break-words">{c.name}</div>
                      )}
                      {c.phone && (
                        <div className="font-mono text-ink-soft">{c.phone}</div>
                      )}
                      {c.relationship && (
                        <div className="mt-1">
                          <span className="text-[10px] uppercase tracking-[0.18em] text-sage-700 bg-sage-100 px-2 py-0.5 rounded-full">
                            {c.relationship}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (patient.emergency_contact_name || patient.emergency_contact_phone) ? (
              <div className="flex items-start gap-3 text-sm text-ink-soft">
                <Phone size={18} className="text-sage-500 mt-0.5" weight="duotone" />
                <div>
                  {patient.emergency_contact_name && (
                    <div className="text-ink font-medium">{patient.emergency_contact_name}</div>
                  )}
                  {patient.emergency_contact_phone && (
                    <div className="font-mono">{patient.emergency_contact_phone}</div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          {patient.notes && (
            <div className="pt-4 border-t border-stone_line">
              <div className="text-xs uppercase tracking-[0.2em] text-ink-muted mb-2">{t('notes')}</div>
              <p className="text-sm text-ink-soft leading-relaxed">{patient.notes}</p>
            </div>
          )}
        </aside>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-stone_line p-8 shadow-soft">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-2xl text-ink">{t('assigned_devices')}</h2>
              <span className="text-xs text-ink-muted">{devices.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {devices.map((d) => {
                const Icon = deviceIconFrom(catalog, d.type);
                return (
                  <Link
                    key={d.id}
                    to={`/devices/${d.id}`}
                    data-testid={`patient-device-${d.id}`}
                    className="flex items-center gap-4 p-4 rounded-xl border border-stone_line hover:bg-bg-alt/50 hover:border-sage/30 transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-sage-100 text-sage-600 grid place-items-center">
                      <Icon size={20} weight="duotone" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink capitalize">{deviceLabelFrom(catalog, d.type)}</div>
                      <div className="text-xs text-ink-muted font-mono truncate">{d.mac}</div>
                    </div>
                    <div className={`text-xs ${batteryColor(d.battery)}`}>{d.battery}%</div>
                    <StatusChip status={d.online ? 'online' : 'offline'} />
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-stone_line p-8 shadow-soft">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-2xl text-ink">{t('recent_events')}</h2>
              <span className="text-xs text-ink-muted">{t('timeline_24h')}</span>
            </div>
            {events.length === 0 ? (
              <div className="text-sm text-ink-muted py-6">{t('no_results')}</div>
            ) : (
              <ol className="relative border-l border-stone_line ml-3" data-testid="patient-timeline">
                {events.map((ev) => {
                  const Icon = eventIcon(ev.type);
                  return (
                    <li key={ev.id} className="ml-6 pb-6 last:pb-0">
                      <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-white border-2 border-stone_line grid place-items-center">
                        <SeverityDot severity={ev.severity} />
                      </span>
                      <div className="flex items-start gap-3">
                        <Icon size={18} className="text-ink-soft mt-0.5" weight="duotone" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-3 justify-between">
                            <div className="text-sm font-medium text-ink">{t(`event_${ev.type}`)}</div>
                            <div className="text-xs text-ink-muted whitespace-nowrap">
                              {formatDateTime(ev.created_at, lang)} · {rel(ev.created_at)}
                            </div>
                          </div>
                          <div className="text-xs text-ink-soft mt-1">{ev.message}</div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </div>

      {user?.role === 'admin' && <AssignmentCards residentId={id} />}
    </div>
  );
}
